import React, { useState, useCallback } from 'react';

import AudioVisualizer from '../components/AudioVisualizer';
import TranscriptionList from '../components/TranscriptionList';
import useSocket from '../hooks/UseSocket';
import useAudioRecorder from '../hooks/UseAudioRecorder';

import './css/whisper-testbed.css';

function render_status_badge(connected, is_listening, is_speaking) {
	if(!connected) {
		return <span className="status status-disconnected">Disconnected</span>;
	}
	if(is_listening && is_speaking) {
		return <span className="status status-speaking">Speaking</span>;
	}
	if(is_listening) {
		return <span className="status status-listening">Listening</span>;
	}
	return <span className="status status-idle">Idle</span>;
}

function handle_start(set_transcriptions, set_is_listening) {
	set_transcriptions([]);
	set_is_listening(true);
}

function handle_stop(set_is_listening) {
	set_is_listening(false);
}

function handle_threshold_change(event, set_speech_threshold) {
	set_speech_threshold(parseFloat(event.target.value));
}

function render_noise_gate(speech_threshold, set_speech_threshold) {
	return (
		<div className="noise-gate">
			<label className="noise-gate-label" htmlFor="noise-gate-slider">
				{'Noise Gate: ' + speech_threshold.toFixed(3)}
			</label>
			<input
				id="noise-gate-slider"
				className="noise-gate-slider"
				type="range"
				min="0.010"
				max="0.100"
				step="0.005"
				value={speech_threshold}
				onChange={function(event) { handle_threshold_change(event, set_speech_threshold); }}
			/>
		</div>
	);
}

function render_controls(is_listening, connected, set_transcriptions, set_is_listening, speech_threshold, set_speech_threshold, is_speaking) {
	return (
		<div className="controls-container">
			<div className="controls">
				{!is_listening && (
					<button
						className="btn btn-start"
						onClick={function() { handle_start(set_transcriptions, set_is_listening); }}
						disabled={!connected}
					>
						Start
					</button>
				)}
				{is_listening && (
					<button
						className="btn btn-stop"
						onClick={function() { handle_stop(set_is_listening); }}
					>
						Stop
					</button>
				)}
				{render_status_badge(connected, is_listening, is_speaking)}
			</div>
			{render_noise_gate(speech_threshold, set_speech_threshold)}
		</div>
	);
}

function render_visualizer(analyser_ref, is_listening) {
	return (
		<div className="visualizer-container">
			<AudioVisualizer
				analyser_ref={analyser_ref}
				is_active={is_listening}
			/>
		</div>
	);
}

function render_transcriptions(transcriptions) {
	return (
		<div className="transcription-container">
			<h2 className="transcription-heading">Transcription</h2>
			<TranscriptionList transcriptions={transcriptions} />
		</div>
	);
}

function WhisperTestbedView() {
	const [is_listening, set_is_listening] = useState(false);
	const [transcriptions, set_transcriptions] = useState([]);
	const [speech_threshold, set_speech_threshold] = useState(0.025);

	const handle_transcription = useCallback(function(text) {
		set_transcriptions(function(previous) {
			return previous.concat([text]);
		});
	}, []);

	const handle_error = useCallback(function(error_message) {
		console.error('Transcription error: ' + error_message);
	}, []);

	const { connected, send_audio_chunk } = useSocket(handle_transcription, handle_error);
	const { analyser_ref, is_speaking } = useAudioRecorder(is_listening, send_audio_chunk, speech_threshold);

	return (
		<React.Fragment>
			<div className="whisper-testbed">
				<header className="whisper-testbed-header">
					<h1 className="whisper-testbed-title">AI Call Trainer</h1>
				</header>
				<main className="whisper-testbed-main">
					{render_controls(is_listening, connected, set_transcriptions, set_is_listening, speech_threshold, set_speech_threshold, is_speaking)}
					{render_visualizer(analyser_ref, is_listening)}
					{render_transcriptions(transcriptions)}
				</main>
			</div>
		</React.Fragment>
	);
}

export default WhisperTestbedView;
